// Formulaire d'inscription : compte créé, puis redirection vers le
// paiement. Logique métier inchangée ; la présentation est optimisée pour
// tenir en un écran de téléphone, chaque champ en moins étant un
// abandon en moins.
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Field from './Field'
import PayLogos from './PayLogos'
import { useAuth } from '../../lib/AuthContext'
import { goToCheckout } from '../../lib/api'
import { getAttribution, setUserId, track } from '../../lib/tracking'
import { getAffiliate } from '../../lib/affiliate'
import { useT } from '../../lib/i18n'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

// Les montants déclarés à Meta. Ils doublent ceux du serveur
// (CTRL-BACKEND/config/stripe.php) et DOIVENT rester identiques : le
// navigateur et le serveur envoient le même événement, et deux valeurs
// différentes pour un même client faussent l'optimisation.
const PRIX = { monthly: 9.99, yearly: 49.99 }

export default function RegisterForm () {
  const t = useT()
  const { register } = useAuth()
  const navigate = useNavigate()
  // La formule vient de l'URL mais se change ici même : renvoyer quelqu'un
  // sur la grille tarifaire au milieu d'une inscription, c'est le perdre.
  const [params, setParams] = useSearchParams()
  const plan = params.get('plan') === 'yearly' ? 'yearly' : 'monthly'
  // La formule annuelle a ses propres libellés (montant et garanties).
  const annuel = plan === 'yearly'

  const [email, setEmail] = useState('')
  const [pass, setPass] = useState('')
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)

  // Les libellés sont traduits, les valeurs (`v`) ne le sont jamais : elles
  // partent telles quelles au backend et à Stripe.
  const PLANS = [
    { v: 'monthly', label: t.register.monthly, price: t.register.monthlyPrice, per: t.register.monthlyPer },
    // `was` : prix de référence barré (douze mois de mensuel), pour ancrer
    // l'annuel sous le coût réel du mensuel sur un an.
    { v: 'yearly', label: t.register.yearly, price: t.register.yearlyPrice, per: t.register.yearlyPer, was: t.register.yearlyWas, best: t.register.best }
  ]

  function choosePlan (v) {
    // `replace` : le changement de formule ne doit pas empiler des entrées
    // dans l'historique du navigateur.
    setParams({ plan: v }, { replace: true })
  }

  function validate () {
    const e = {}
    if (!EMAIL_RE.test(email)) e.email = t.errors.email
    if (pass.length < 8) e.pass = t.errors.passwordLength
    return e
  }

  async function onSubmit (ev) {
    ev.preventDefault()
    const e = validate()
    setErrors(e)
    if (Object.keys(e).length) return

    setLoading(true)
    try {
      // 1. Création du compte (sans aucun essai accordé).
      //    Les signaux publicitaires voyagent avec : c'est le seul moment
      //    où le navigateur du client peut encore les fournir.
      // Plus de champ « nom » : un formulaire de moins à remplir. Le
      // serveur en exige un, on le déduit de l'adresse — ça reste un
      // libellé d'affichage, rien de plus.
      const created = await register({
        name: email.trim().split('@')[0].slice(0, 60) || t.register.fallbackName,
        email: email.trim(), password: pass, plan,
        ...getAttribution(),
        // Le code d'affilié gardé depuis son lien ?via= (vide sinon).
        ...getAffiliate()
      })

      // Le même identifiant que celui que le serveur joindra au Purchase :
      // sans lui, les deux événements ne se rapprochent que par l'e-mail.
      if (created?.id) setUserId(created.id)

      // L'inscription. C'est l'événement sur lequel on optimisera au
      // lancement, faute d'avoir assez de Purchase pour sortir de la phase
      // d'apprentissage. `register.php` l'envoie AUSSI depuis le serveur avec
      // le même identifiant `signup_<id>` : Meta déduplique, et le signal
      // survit aux bloqueurs de publicité.
      // ⚠️ Le montant et la devise doivent rester IDENTIQUES à ceux du
      // serveur (CTRL-BACKEND/config/stripe.php : 9,99 et 49, en USD, depuis
      // le 26/09/2026). Deux valeurs différentes pour le même client faussent
      // l'optimisation de Meta.
      if (created?.id) {
        track('CompleteRegistration',
          { currency: 'USD', value: PRIX[plan], content_name: 'SELFCNTRL' },
          'signup_' + created.id)
      }

      // 2. Direction le paiement (débité tout de suite, plus d'essai).
      try {
        track('InitiateCheckout', {
          currency: 'USD',
          value: PRIX[plan],
          content_name: 'SELFCNTRL'
        })
        await goToCheckout(plan)
        return // la page part sur le paiement, on ne rend plus rien
      } catch (payErr) {
        // Le compte EXISTE déjà : on ne le perd pas. On dépose l'utilisateur
        // dans son espace, où il pourra relancer le paiement.
        navigate('/dashboard/abonnement')
        return
      }
    } catch (err) {
      // 409 = e-mail déjà pris : on pointe le champ concerné.
      if (err.status === 409) setErrors({ email: t.errors.emailTaken })
      else setErrors({ form: err.message })
      setLoading(false)
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate className="rg-f">
      {/* La formule : deux cartes, le prix par jour en dessous. La valeur
          (`v`) part telle quelle au backend et à Stripe. */}
      <div className="rg-plans" role="radiogroup" aria-label={t.register.planGroup}>
        {PLANS.map(p => (
          <button type="button" key={p.v} role="radio" aria-checked={plan === p.v}
            className={`rg-plan${plan === p.v ? ' is-on' : ''}`}
            onClick={() => choosePlan(p.v)}>
            <span className="rg-plan__radio" aria-hidden="true" />
            <span className="rg-plan__l">{p.label}{p.best && <em>{p.best}</em>}</span>
            <span className="rg-plan__p">{p.price}<small>{p.per}</small></span>
            <span className="rg-plan__d">
              {p.was && <s>{p.was}</s>}
              {p.v === 'yearly' ? t.register.perDayYearly : t.register.perDayMonthly}
            </span>
          </button>
        ))}
      </div>

      <Field
        id="email" label={t.register.emailLabel} type="email"
        value={email} onChange={setEmail}
        placeholder={t.register.emailPlaceholder} autoComplete="email"
        enterKeyHint="next"
        error={errors.email}
      />

      <Field
        id="pass" label={t.register.passLabel} type="password"
        value={pass} onChange={setPass}
        placeholder={t.register.passPlaceholder} autoComplete="new-password"
        enterKeyHint="go"
        error={errors.pass}
      />

      {errors.form && <p className="u-fld__err u-fld__err--form">{errors.form}</p>}

      <button type="submit" className="rg-go" disabled={loading}>
        <span>{loading
          ? t.register.submitting
          : (annuel ? t.register.submitYear : t.register.submit)}</span>
        <b>{annuel ? t.register.yearlyPrice : t.register.monthlyPrice} →</b>
      </button>

      <PayLogos label={t.register.pay} />

      {/* Les vraies garanties, sous le bouton. Rien d'inventé : chaque ligne
          existe dans les CGV / la politique de remboursement. */}
      <ul className="rg-warr">
        {(annuel ? t.register.guarYear : t.register.guar).map((g, i) => (
          <li key={i}>{g}</li>
        ))}
      </ul>

    </form>
  )
}
